/**
 * <custom-countdown> — shows the days, hours and minutes left until a target
 * date/time. The target is a wall-clock time in an IANA time zone (e.g.
 * "Europe/Madrid"), so it stays correct whatever the visitor's own time zone.
 * When the target is reached (or the date is invalid) every unit shows 00 and
 * the element gets the `custom-countdown--finished` class, which the block's
 * CSS uses to swap the button label and optionally hide the timer.
 */
(() => {
  if (customElements.get('custom-countdown')) return;

  const MINUTE = 60 * 1000;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  /** Offset (ms) between the given time zone and UTC at a given instant. */
  const getTimeZoneOffset = (timestamp, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(timestamp));

    const values = {};
    parts.forEach(({ type, value }) => {
      values[type] = Number(value);
    });

    const asUtc = Date.UTC(values.year, values.month - 1, values.day, values.hour, values.minute, values.second);

    return asUtc - Math.floor(timestamp / 1000) * 1000;
  };

  /** Converts "YYYY-MM-DD" + "HH:MM" in `timeZone` to a UTC timestamp, or null. */
  const parseTarget = (date, time, timeZone) => {
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec((date || '').trim());
    const timeMatch = /^(\d{1,2}):(\d{2})$/.exec((time || '00:00').trim());

    if (!dateMatch || !timeMatch) return null;

    const [, year, month, day] = dateMatch.map(Number);
    const [, hours, minutes] = timeMatch.map(Number);

    if (month < 1 || month > 12 || day < 1 || day > 31 || hours > 23 || minutes > 59) return null;

    const wallClockAsUtc = Date.UTC(year, month - 1, day, hours, minutes);

    try {
      // Two passes so the offset is the one in effect at the target (handles DST changes).
      let timestamp = wallClockAsUtc - getTimeZoneOffset(wallClockAsUtc, timeZone);
      timestamp = wallClockAsUtc - getTimeZoneOffset(timestamp, timeZone);
      return timestamp;
    } catch (error) {
      // Invalid time zone name: fall back to the visitor's local time.
      return new Date(year, month - 1, day, hours, minutes).getTime();
    }
  };

  const pad = (value) => String(value).padStart(2, '0');

  class CustomCountdown extends HTMLElement {
    connectedCallback() {
      this.target = parseTarget(this.dataset.date, this.dataset.time, this.dataset.timezone || undefined);
      this.days = this.querySelector('[data-unit="days"]');
      this.hours = this.querySelector('[data-unit="hours"]');
      this.minutes = this.querySelector('[data-unit="minutes"]');

      this.update();

      if (this.target !== null && this.target > Date.now()) {
        this.interval = setInterval(() => this.update(), 1000);
      }
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    update() {
      const remaining = this.target === null ? 0 : Math.max(0, this.target - Date.now());

      // Round minutes up so the counter only reads 00 : 00 : 00 once the time is reached.
      const totalMinutes = Math.ceil(remaining / MINUTE);
      const days = Math.floor(totalMinutes / (DAY / MINUTE));
      const hours = Math.floor((totalMinutes % (DAY / MINUTE)) / (HOUR / MINUTE));
      const minutes = totalMinutes % (HOUR / MINUTE);

      if (this.days) this.days.textContent = pad(days);
      if (this.hours) this.hours.textContent = pad(hours);
      if (this.minutes) this.minutes.textContent = pad(minutes);

      if (this.hasAttribute('data-hide-zero-units')) {
        // Only leading units are hidden, so "2 days 00 hrs 05 min" keeps its hours.
        this.toggleUnit('days', days === 0);
        this.toggleUnit('hours', days === 0 && hours === 0);
      }

      if (remaining === 0) {
        clearInterval(this.interval);
        this.classList.add('custom-countdown--finished');
        this.dispatchEvent(new CustomEvent('countdown:finished', { bubbles: true }));
      }
    }

    /** Hides a unit together with the separator that follows it. */
    toggleUnit(unit, hidden) {
      this.querySelectorAll(`[data-unit-group="${unit}"]`).forEach((element) => {
        element.hidden = hidden;
      });
    }
  }

  customElements.define('custom-countdown', CustomCountdown);
})();
